import { useEffect, useState } from 'react';
import { adjustDate, getConfirmMonthPreview, ConfirmMonthPreview, EvidenceSource, TodayStatus, AdminMonth } from '../api/adminApi';
import ConfirmMonthChecklist from './ConfirmMonthChecklist';
import ResetLocationPanel from './ResetLocationPanel';
import styles from './AdjustDateModal.module.css';

const HIJRI_MONTHS = [
  '', 'Muharram', 'Safar', "Rabi' al-Awwal", "Rabi' al-Thani",
  'Jumada al-Awwal', 'Jumada al-Thani', 'Rajab', "Sha'ban",
  'Ramadan', 'Shawwal', "Dhu al-Qi'dah", 'Dhu al-Hijjah',
];

interface Props {
  locationCode: string;
  locationName: string;
  status: TodayStatus | null;
  months: AdminMonth[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function AdjustDateModal({ locationCode, locationName, status, months, onClose, onSuccess }: Props) {
  const availableYears = Array.from(new Set(months.map((m) => m.year))).sort();
  const currentYear = status?.date.year || availableYears[0] || 1447;
  const currentMonthIdx = status?.date.month.index || 1;

  const confirmedMonths = months.filter((m) => m.status === 'CONFIRMED');

  const [targetYear, setTargetYear] = useState(currentYear);
  const [targetMonth, setTargetMonth] = useState(currentMonthIdx);
  const [newStartDate, setNewStartDate] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [evidence, setEvidence] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [preview, setPreview] = useState<ConfirmMonthPreview | null>(null);

  async function refreshPreview() {
    try {
      setPreview(await getConfirmMonthPreview(locationCode, targetYear, targetMonth));
    } catch {
      setPreview(null);
    }
  }

  useEffect(() => {
    refreshPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationCode, targetYear, targetMonth]);

  const selectedMonth = months.find(
    (m) => m.year === targetYear && m.monthIndex === targetMonth
  );

  const prevStartDate = selectedMonth?.startDate;
  const prevMonthName = HIJRI_MONTHS[targetMonth] ?? `Month ${targetMonth}`;
  const currentMonthName = status?.date.month.en ?? HIJRI_MONTHS[currentMonthIdx];

  const previewText = newStartDate && reason
    ? `Changing ${locationName} — ${prevMonthName} ${targetYear} start date from ${prevStartDate ?? 'unknown'} to ${newStartDate}`
    : null;

  // Reuses the same start-date/reason/evidence fields as the normal adjustment form,
  // so filling them in once works for either action.
  const resetConfirmArgs = newStartDate
    ? {
        year: targetYear,
        month: targetMonth,
        startDate: newStartDate,
        source: reason.trim() || evidence.trim() || 'Manual initialization',
        sources: evidence.trim() ? [{ url: evidence.trim(), language: 'en' }] : undefined,
      }
    : null;

  async function handleSubmit() {
    if (!newStartDate) { setError('New start date is required.'); return; }
    if (!reason.trim()) { setError('Reason is required for all adjustments.'); return; }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(newStartDate)) { setError('Date must be in YYYY-MM-DD format.'); return; }

    setSubmitting(true);
    setError('');
    try {
      const sources: EvidenceSource[] = evidence.trim()
        ? [{ url: evidence.trim(), language: 'en' }]
        : [];
      await adjustDate({
        locationCode,
        year: targetYear,
        month: targetMonth,
        newStartDate,
        reason: reason.trim(),
        note: note.trim() || undefined,
        sources: sources.length > 0 ? sources : undefined,
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.title}>Adjust Date</h2>
            <p className={styles.subtitle}>{locationName} · currently day {status?.date.day ?? '?'} {currentMonthName}</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.body}>
          {/* Summary — what's actually going on with this country's confirmation history */}
          {preview && (
            preview.missingMonths.length > 0 ? (
              <ConfirmMonthChecklist
                locationCode={locationCode}
                missingMonths={preview.missingMonths}
                onConfirmed={refreshPreview}
                hint={
                  <>
                    {preview.previousConfirmed ? (
                      <>
                        Most recent confirmed month on record: <strong>{preview.previousConfirmed.monthName.en} {preview.previousConfirmed.year}</strong>{' '}
                        (started {preview.previousConfirmed.startDate}).
                      </>
                    ) : (
                      <>No confirmed month on record yet.</>
                    )}{' '}
                    {preview.missingMonths.length} month{preview.missingMonths.length > 1 ? 's' : ''} between then and{' '}
                    {HIJRI_MONTHS[targetMonth]} {targetYear} {preview.missingMonths.length > 1 ? 'are' : 'is'} not yet confirmed —
                    confirm them below, or continue with the adjustment further down if that's what needs fixing instead.
                  </>
                }
              />
            ) : preview.previousConfirmed ? (
              <div className={`${styles.summary} ${styles.summaryOk}`}>
                Most recent confirmed month on record: <strong>{preview.previousConfirmed.monthName.en} {preview.previousConfirmed.year}</strong>{' '}
                (started {preview.previousConfirmed.startDate}). No gap to {HIJRI_MONTHS[targetMonth]} {targetYear}.
              </div>
            ) : null
          )}

          {/* Target month */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Target Month</label>
            <div className={styles.monthRow}>
              <select
                className={styles.select}
                value={targetYear}
                onChange={(e) => setTargetYear(Number(e.target.value))}
              >
                {availableYears.map((y) => (
                  <option key={y} value={y}>{y} AH</option>
                ))}
              </select>
              <select
                className={styles.select}
                value={targetMonth}
                onChange={(e) => setTargetMonth(Number(e.target.value))}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((i) => (
                  <option key={i} value={i}>{HIJRI_MONTHS[i]} ({i})</option>
                ))}
              </select>
            </div>
            {prevStartDate && (
              <p className={styles.hint}>Current start date: <strong>{prevStartDate}</strong></p>
            )}
            {!prevStartDate && confirmedMonths.length > 0 && (
              <p className={styles.hintWarn}>This month has not been confirmed yet.</p>
            )}
          </div>

          {/* New start date */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>New Gregorian Start Date <span className={styles.required}>*</span></label>
            <input
              className={styles.input}
              type="date"
              value={newStartDate}
              onChange={(e) => setNewStartDate(e.target.value)}
            />
          </div>

          {/* Reason */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Reason <span className={styles.required}>*</span></label>
            <select
              className={styles.select}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="">Select a reason…</option>
              <option value="Authority correction">Authority correction</option>
              <option value="Operational mistake">Operational mistake</option>
              <option value="Timezone issue">Timezone issue</option>
              <option value="Emergency adjustment">Emergency adjustment</option>
              <option value="Data entry error">Data entry error</option>
              <option value="Source disagreement resolved">Source disagreement resolved</option>
            </select>
          </div>

          {/* Note */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Note <span className={styles.optional}>(optional)</span></label>
            <textarea
              className={styles.textarea}
              placeholder="Add any additional context…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
            />
          </div>

          {/* Evidence */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Evidence URL <span className={styles.optional}>(optional)</span></label>
            <input
              className={styles.input}
              type="url"
              placeholder="https://example.com/source"
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
            />
          </div>

          {/* Preview */}
          {previewText && (
            <div className={styles.preview}>
              <span className={styles.previewIcon}>⚠️</span>
              <span className={styles.previewText}>{previewText}</span>
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.actions}>
            <button className={styles.cancelBtn} onClick={onClose} disabled={submitting}>Cancel</button>
            <button
              className={styles.confirmBtn}
              onClick={handleSubmit}
              disabled={!newStartDate || !reason || submitting}
            >
              {submitting ? 'Saving…' : 'Save Adjustment'}
            </button>
          </div>

          {preview && preview.previousConfirmed && (
            <ResetLocationPanel
              locationCode={locationCode}
              locationName={locationName}
              deletingMonthLabel={`${preview.previousConfirmed.monthName.en} ${preview.previousConfirmed.year}`}
              targetLabel={`${HIJRI_MONTHS[targetMonth]} ${targetYear}`}
              confirmArgs={resetConfirmArgs}
              onSuccess={onSuccess}
            />
          )}
        </div>
      </div>
    </div>
  );
}
